import React, { FC } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import axios from '@/lib/axios';
import { toast } from 'react-toastify';
import { FaCircleCheck } from 'react-icons/fa6';
import { CiWarning } from 'react-icons/ci';
import { API_PRODUCT_AUTHENTIC_REVIEWS_SUBMIT } from '@/constant/apiRout';

interface AuthenticityModalProps {
  mutate: () => void;
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
  productId: number;
  orderId: number;
}

// 1. We'll use strings for the form state as requested.
type FormValues = {
  authenticity: '1' | '0';
  note?: string;
};

const AuthenticityModal: FC<AuthenticityModalProps> = ({
  show,
  mutate,
  setShow,
  productId,
  orderId,
}) => {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    mode: 'onChange',
  });

  const authenticityValue = watch('authenticity');
  // This will now reliably log '1' or '0' every time you click.
  console.log('Current authenticity value:', authenticityValue);

  const onSubmit: SubmitHandler<FormValues> = async (data) => {
    // 2. Convert the string to a number right before sending.
    const submissionData = {
      ...data,
      authenticity: Number(data.authenticity),
    };

    const requestBody = {
      product_id: productId,
      order_id: orderId,
      is_authentic: Number(data.authenticity),
      description: data.note || '',
    };
    try {
      const res = await axios.post(
        API_PRODUCT_AUTHENTIC_REVIEWS_SUBMIT,
        requestBody
      );
      if (res.data.result) {
        mutate();
        setShow(false);
        toast.success(res?.data?.message);
      } else {
        toast.error(res?.data?.message);
      }
    } catch (error: any) {
      toast.error(error?.message);
    }
  };

  const handleClose = () => {
    reset();
    setShow(false);
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Was your product authentic?</Modal.Title>
      </Modal.Header>

      <Form noValidate onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          <Form.Group className="mb-4" controlId="authenticity-group">
            {/* The outer label for the group is fine. */}
            <Form.Label className="fw-bold">Your experience*</Form.Label>
            <div className="d-grid gap-2">
              {/* --- THE FIX: Using plain <label> instead of <Form.Label> --- */}

              {/* Option 1: YES */}
              <>
                <Form.Check
                  type="radio"
                  id="auth-yes"
                  value="1" // Value is now a string
                  className="d-none"
                  {...register('authenticity', {
                    required: 'Please select an option.',
                    // 3. Removed `valueAsNumber: true`
                  })}
                />
                {/* 4. Use a standard HTML <label> tag. This is the key fix. */}
                <label
                  htmlFor="auth-yes"
                  className={`text-center mb-0 py-3 w-100 trk-btn trk-btn--outline3 ${
                    // We add `btn` class manually
                    authenticityValue === '1' // 5. Compare against string '1'
                      ? '   text-danger border-danger bg-none'
                      : ''
                  }`}
                >
                  <FaCircleCheck scale={22} className="me-2" />
                  Yes, it's 100% authentic
                </label>
              </>

              {/* Option 2: NO */}
              <>
                <Form.Check
                  type="radio"
                  id="auth-no"
                  value="0" // Value is now a string
                  className="d-none"
                  {...register('authenticity')} // `required` only needs to be set once per field name
                />
                <label
                  htmlFor="auth-no"
                  className={`text-center mb-0 py-3 w-100 trk-btn trk-btn--outline3 ${
                    // We add `btn` class manually
                    authenticityValue === '0' // 5. Compare against string '1'
                      ? ' text-danger border-danger bg-none'
                      : ''
                  }`}
                >
                  <CiWarning scale={24} className="me-2 fw-bold" />
                  No, I have concerns
                </label>
              </>
            </div>
            {errors.authenticity && (
              <Form.Text className="text-danger mt-1 d-block">
                {errors.authenticity.message}
              </Form.Text>
            )}
          </Form.Group>

          {/* Note Textarea */}
          <Form.Group className="mb-3" controlId="note">
            <Form.Label className="fw-bold">
              Note
              {authenticityValue === '0' && ( // Compare against string '0'
                <span className="text-danger ms-1">*</span>
              )}
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Tell us what you liked or what seemed off. it matched what i have use before / Packaging looked different..."
              isInvalid={!!errors.note}
              {...register('note', {
                required:
                  authenticityValue === '0'
                    ? 'A note is required when you have concerns.'
                    : false,
              })}
            />
            {errors.note && (
              <Form.Control.Feedback type="invalid">
                {errors.note.message}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <button className="trk-btn trk-btn--outline " onClick={handleClose}>
            Cancel
          </button>
          <button className="trk-btn trk-btn--primary " type="submit">
            Submit Feedback
          </button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default AuthenticityModal;
